import React, { useState, useMemo } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  LinearProgress,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Card,
  CardContent,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { useMultiTableAnalysis } from '../../hooks/useMultiTableAnalysis'
import { useMultiTableAnalysisChat } from '../../hooks/useMultiTableAnalysisChat'
import ChatSession, { ChatMessage } from '../chat/ChatSession'
import MultiTableGraph from './MultiTableGraph'
import { GraphConfig } from '../platinum-graphs/types'

interface MultiTableAnalysisDrawerProps {
  open: boolean
  onClose: () => void
  customer: string
  job: string
  multiTableId: string
  multiTableName?: string
  /** The persona picker's list: the customer's personas library,
   *  Company-level then the user's own (#1523). It used to be the two
   *  built-in names only. */
  personas: string[]
}

// Report type for the dropdown
type ReportType = 'combined' | string // 'combined' or item ID

const MultiTableAnalysisDrawer: React.FC<MultiTableAnalysisDrawerProps> = ({
  open,
  onClose,
  customer,
  job,
  multiTableId,
  multiTableName,
  personas,
}) => {
  const [selectedReport, setSelectedReport] = useState<ReportType>('combined')
  const [graphScreenshots, setGraphScreenshots] = useState<Map<number, string>>(new Map())

  // Analysis hook
  const analysis = useMultiTableAnalysis({
    customer,
    job,
    multiTableId,
  })

  // Chat hook - pass the selected report context
  const chat = useMultiTableAnalysisChat({
    customer,
    job,
    multiTableId,
    analysisId: analysis.selectedAnalysisId,
    isAnalysisCompleted: analysis.isCompleted,
    reportType: selectedReport === 'combined' ? 'combined' : 'individual',
    tableItemId: selectedReport === 'combined' ? undefined : selectedReport,
  })

  // Check if we have existing analyses
  const hasAnalyses = analysis.analyses.length > 0
  const latestAnalysis = hasAnalyses ? analysis.analyses[0] : null

  // Auto-select latest analysis if we don't have one selected
  React.useEffect(() => {
    if (open && latestAnalysis && !analysis.selectedAnalysisId) {
      analysis.setSelectedAnalysisId(latestAnalysis.id)
    }
  }, [open, latestAnalysis, analysis.selectedAnalysisId])

  // Reset to combined report when analysis changes
  React.useEffect(() => {
    setSelectedReport('combined')
  }, [analysis.selectedAnalysisId])

  const handleStartAnalysis = async (persona?: string) => {
    try {
      await analysis.startAnalysis({ persona: persona || chat.selectedPersona })
    } catch (err) {
      // Error handled by hook
    }
  }

  // Handle persona change - switch to existing analysis or prompt to generate
  const handlePersonaChange = (newPersona: string) => {
    chat.setSelectedPersona(newPersona)

    // First, try to switch to an existing completed analysis for this persona
    if (analysis.switchToPersona(newPersona)) {
      // Successfully switched to existing analysis - no action needed
      return
    }

    // No existing analysis for this persona - prompt to generate
    if (window.confirm(`No analysis exists for "${newPersona}" persona. Generate one now?`)) {
      handleStartAnalysis(newPersona)
    }
  }

  // Get the current report content based on selection
  const getCurrentReportContent = (): { title: string; content: string; error?: string } => {
    if (selectedReport === 'combined') {
      return {
        title: 'Combined Report',
        content: analysis.analysis?.combined_report || 'No report available.',
      }
    }

    // Find the selected item
    const item = analysis.items.find(i => i.id === selectedReport)
    if (!item) {
      return { title: 'Unknown', content: 'Report not found.' }
    }

    const title = item.display_name || item.table_name || 'Table Analysis'

    if (item.state === 'failed') {
      return { title, content: '', error: item.error || 'Analysis failed for this table' }
    }

    if (item.state !== 'completed' || !item.analysis) {
      return { title, content: 'No analysis available for this table.' }
    }

    return { title, content: item.analysis }
  }

  // Render progress view
  const renderProgress = () => {
    const progress = analysis.progress
    const percentDone = progress?.percent_done || 0
    const completed = progress?.completed || 0
    const total = progress?.total || 0
    const currentTable = progress?.current_table || ''

    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Analyzing Tables...
        </Typography>
        <Box sx={{ my: 3 }}>
          <LinearProgress
            variant="determinate"
            value={percentDone}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {completed} of {total} tables completed ({percentDone}%)
          </Typography>
        </Box>
        {currentTable && (
          <Typography variant="body2" color="text.secondary">
            Currently analyzing: {currentTable}
          </Typography>
        )}
        <CircularProgress sx={{ mt: 3 }} />
      </Box>
    )
  }

  // Render start analysis view
  const renderStartView = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>
        Multi-Table Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Analyze all tables in this multi-table collection and generate a comprehensive executive report.
      </Typography>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Persona</InputLabel>
        <Select
          value={chat.selectedPersona}
          label="Persona"
          onChange={(e) => chat.setSelectedPersona(e.target.value)}
        >
          {personas.map((persona) => (
            <MenuItem key={persona} value={persona}>
              {persona}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        color="info"
        startIcon={<PlayArrowIcon />}
        onClick={() => handleStartAnalysis()}
        disabled={analysis.startAnalysisLoading}
      >
        {analysis.startAnalysisLoading ? 'Starting...' : 'Start Analysis'}
      </Button>
    </Box>
  )

  // Render error view
  const renderError = () => (
    <Box sx={{ p: 3 }}>
      <Alert severity="error" sx={{ mb: 2 }}>
        {analysis.analysis?.error || 'Analysis failed'}
      </Alert>
      <Button
        variant="contained"
        color="info"
        startIcon={<RefreshIcon />}
        onClick={() => handleStartAnalysis()}
      >
        Retry Analysis
      </Button>
    </Box>
  )

  // Create messages array with report as first message
  const messagesWithReport = useMemo((): ChatMessage[] => {
    const reportData = getCurrentReportContent()
    const reportMessage: ChatMessage = {
      role: 'assistant',
      content: reportData.error || reportData.content,
    }
    // Add a marker to identify the report message
    ;(reportMessage as ChatMessage & { isReport?: boolean }).isReport = true
    return [reportMessage, ...chat.messages]
  }, [analysis.analysis?.combined_report, analysis.items, selectedReport, chat.messages])

  // Custom renderer for messages - handles both the report (index 0) and graph tool calls
  const renderMessage = (message: ChatMessage, index: number): React.ReactNode | undefined => {
    // Handle first message (the report) specially
    if (index === 0) {
      const reportData = getCurrentReportContent()

      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1, maxWidth: '95%' }}>
            <Avatar src="/img/platinum-icon.png" alt="Analysis" sx={{ width: 24, height: 24 }} />
            <Card sx={{ borderRadius: 2, border: '1px solid #00b2ff' }}>
              <CardContent sx={{
                p: 1.5,
                '&:last-child': { paddingBottom: 1.5 },
                '& > :first-of-type': { marginTop: 0 },
                '& > :last-child': { marginBottom: 0 },
                '& p': { margin: 0, fontSize: '0.875rem' },
                '& h1': { fontSize: '1.25rem', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.5em' },
                '& h2': { fontSize: '1.15rem', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.5em' },
                '& h3': { fontSize: '1.05rem', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.5em' },
                '& h4': { fontSize: '0.95rem', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.5em' },
                '& img': { maxWidth: '100%' },
              }}>
                {reportData.error ? (
                  <Alert severity="error">{reportData.error}</Alert>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {reportData.content}
                  </ReactMarkdown>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )
    }

    // Check for graph tool calls in assistant messages
    if (message.role === 'assistant' && message.tool_calls && message.tool_calls.length > 0) {
      const graphCalls = message.tool_calls.filter(tc => tc.function.name === 'render_graph')

      if (graphCalls.length > 0) {
        return (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1, maxWidth: '85%' }}>
              <Avatar src="/img/platinum-icon.png" alt="Insights" sx={{ width: 24, height: 24 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                {/* Render text content if present */}
                {message.content && (
                  <Card sx={{ borderRadius: 2, border: '1px solid #00b2ff' }}>
                    <CardContent sx={{
                      p: 1.5,
                      '&:last-child': { paddingBottom: 1.5 },
                      '& > :first-of-type': { marginTop: 0 },
                      '& > :last-child': { marginBottom: 0 },
                      '& p': { margin: 0, fontSize: '0.875rem' },
                    }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    </CardContent>
                  </Card>
                )}

                {/* Render graphs */}
                {graphCalls.map((graphCall, graphIndex) => {
                  try {
                    const graphConfig: GraphConfig & { tablePath?: string } = JSON.parse(graphCall.function.arguments)
                    const tablePath = graphConfig.tablePath

                    if (!tablePath) {
                      return (
                        <Card key={graphIndex} sx={{ borderRadius: 2, border: '1px solid #ff0000' }}>
                          <CardContent sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="error">
                              Missing table path for graph
                            </Typography>
                          </CardContent>
                        </Card>
                      )
                    }

                    return (
                      <MultiTableGraph
                        key={graphIndex}
                        customer={customer}
                        job={job}
                        tablePath={tablePath}
                        graphConfig={graphConfig}
                        explanation={graphConfig.explanation}
                        messageIndex={index}
                        graphIndex={graphIndex}
                        graphScreenshots={graphScreenshots}
                        setGraphScreenshots={setGraphScreenshots}
                        onClose={onClose}
                      />
                    )
                  } catch (e) {
                    console.error('Failed to parse graph config:', e)
                    return (
                      <Card key={graphIndex} sx={{ borderRadius: 2, border: '1px solid #ff0000' }}>
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="error">
                            Failed to parse graph configuration
                          </Typography>
                        </CardContent>
                      </Card>
                    )
                  }
                })}
              </Box>
            </Box>
          </Box>
        )
      }
    }

    // Let ChatSession handle default rendering
    return undefined
  }

  // Report dropdown as additional control for ChatSession
  const reportDropdown = (
    <Box sx={{ flex: 1, minWidth: '120px' }}>
      <FormControl fullWidth size="small">
        <InputLabel sx={{ fontSize: '0.70rem' }}>Report</InputLabel>
        <Select
          value={selectedReport}
          label="Report"
          onChange={(e) => setSelectedReport(e.target.value as ReportType)}
          sx={{ fontSize: '0.70rem' }}
          MenuProps={{ PaperProps: { sx: { fontSize: '0.70rem' } } }}
        >
          <MenuItem value="combined" sx={{ fontSize: '0.70rem' }}>
            <strong>Combined Report</strong>
          </MenuItem>
          <Divider />
          {analysis.items.map((item) => (
            <MenuItem key={item.id} value={item.id} sx={{ fontSize: '0.70rem' }}>
              {item.display_name || item.table_name || 'Table'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )

  // Render completed analysis using ChatSession
  const renderCompleted = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <ChatSession
          messages={messagesWithReport}
          onSendMessage={chat.send}
          onResetChat={chat.resetChat}
          isLoading={chat.isLoading}
          error={chat.error}
          showHeader={false}
          placeholder="Ask about this report..."
          showAdvancedMode={false}
          personas={personas}
          selectedPersona={chat.selectedPersona}
          onPersonaChange={handlePersonaChange}
          renderMessage={renderMessage}
          additionalControls={reportDropdown}
        />
      </Box>
    )
  }

  // Determine what to render
  const renderContent = () => {
    if (analysis.analysesLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )
    }

    if (analysis.isRunning) {
      return renderProgress()
    }

    if (analysis.isFailed) {
      return renderError()
    }

    if (analysis.isCompleted) {
      return renderCompleted()
    }

    return renderStartView()
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: '50%',
          minWidth: 400,
          maxWidth: 800,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src="/img/platinum-icon.png" alt="Analysis" sx={{ width: 32, height: 32 }} />
          <Typography variant="h6">
            {multiTableName || 'Multi-Table Analysis'}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {renderContent()}
      </Box>
    </Drawer>
  )
}

export default MultiTableAnalysisDrawer
